import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Create the context
const ThemeContext = createContext();

// Create a provider component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(localStorage.getItem('theme') ?? 'light');

  useEffect(() => {
    const htmlElement = document.documentElement;

    htmlElement.setAttribute('data-theme-mode', theme);
    htmlElement.setAttribute('data-header-styles', theme);
  }, [theme]);

  // Define your theme object
  const selectBoxTheme = {
    container: (provided) => ({
      ...provided,
      color: theme==='light' ? '#333335' : '#FFFFFF',
      backgroundColor: theme==='light' ? '#FFFFFF' : '#232628',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: theme==='light' ? '#FFFFFF' : '#444444',
      borderColor: state.isFocused
        ? (theme==='light' ? '#2a3c7d' : '#5b6fb3')
        : (theme==='light' ? '#dde3ef' : '#313335'),
      borderRadius: '0.5rem',
      minHeight: '2.5rem',
      boxShadow: state.isFocused
        ? (theme==='light' ? '0 0 0 0.2rem rgba(42, 60, 125, 0.12)' : 'none')
        : 'none',
      '&:hover': {
        borderColor: theme==='light' ? '#b8c5e6' : '#5b6fb3',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? (theme==='light' ? '#555' : 'lightblue') : (theme==='light' ? '#FFFFFF' : '#444444'),
      color: state.isSelected ? (theme==='light' ? 'black' : 'white') : 'inherit',
    }),
  };

  // Function to toggle between light and dark themes
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
    localStorage.setItem('theme', theme === 'light' ? 'dark' : 'light');
  };

  // Provide the theme state and toggleTheme function to the components
  const contextValue = useMemo(() => ({
    theme,
    toggleTheme,
    selectBoxTheme
  }), [theme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Create a custom hook to consume the context
export const useTheme = () => {
  return useContext(ThemeContext);
};
